import React, { createContext, useState, useContext, useEffect } from 'react';
import transactionService from '../services/transactionService';
import { useAuth } from './AuthContext';

const GameContext = createContext(null);

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

export const GameProvider = ({ children }) => {
  const { user } = useAuth();
  const [lastLoadTime, setLastLoadTime] = useState(0);
  const [gameState, setGameState] = useState({
    // Financial Status - only real data from backend API
    cash: 0, // Will be populated from /api/transactions/portfolio/{userId}
    savingsBalance: 0, // Will be populated from portfolio.deposit
    loan: 0, // Will be populated from portfolio.loan
    totalInvested: 0, // Will be populated from portfolio.totalAssetsValue
    portfolio: [], // Investment holdings
    
    // Interest Rates (GAME.MD specifications)
    loanInterestRate: 0.04 / 365, // 4% annual = daily compound
    savingsInterestRate: 0.03 / 365, // 3% annual = daily compound
    
    // Experience System
    experienceLevel: 1, // Starting level: 1 (Beginner)
    achievementPoints: 0, // Starting points: 0
    xpCurrent: 0, // Current XP progress
    xpToNextLevel: 100, // XP needed for level 2
    
    // Game Status
    tutorialStatus: 'available', // Tutorial available
    gameDay: 1, // Current day in simulation
    lastUpdate: new Date().toISOString().split('T')[0], // Last update date
    
    // Trading History
    transactions: [], // All buy/sell transactions
    realizedPnL: 0, // Total realized profit/loss
    
    // Achievement tracking
    achievements: [],
    streaks: {
      dailyLogin: 0,
      profitableDays: 0
    }
  });

  // Get user-specific localStorage key
  const getGameStorageKey = () => {
    const userId = user?._id || user?.id;
    if (!userId) return null;
    return `kidsInvestmentGame_${userId}`;
  };

  // Clear previous user's data when user changes
  const clearPreviousUserData = () => {
    // Clear any non-user-specific game data
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('kidsInvestmentGame') && key !== getGameStorageKey()) {
        console.log('🧹 Clearing previous user game data:', key);
        localStorage.removeItem(key);
      }
    });
  };

  // Load saved game state when user is available
  useEffect(() => {
    const userId = user?._id || user?.id;
    if (!userId) {
      console.log('🎮 No user available, using default game state');
      return;
    }

    console.log('🎮 Loading game state for user:', userId);
    clearPreviousUserData();
    
    const storageKey = getGameStorageKey();
    const savedGame = localStorage.getItem(storageKey);
    
    if (savedGame) {
      try {
        const parsedGame = JSON.parse(savedGame);
        console.log('🎮 Loaded saved game state:', parsedGame);
        setGameState(prevState => ({
          ...prevState,
          ...parsedGame
        }));
      } catch (error) {
        console.error('Error loading saved game:', error);
      }
    } else {
      console.log('🎮 No saved game found, using default state');
    }
  }, [user?._id, user?.id]);

  // Save game state whenever it changes (only if user is logged in)
  useEffect(() => {
    const storageKey = getGameStorageKey();
    if (storageKey && user?._id) {
      console.log('🎮 Saving game state for user:', user._id);
      localStorage.setItem(storageKey, JSON.stringify(gameState));
    }
  }, [gameState, user?._id]);

  // Calculate net worth (assets - liabilities)
  const calculateNetWorth = () => {
    return gameState.cash + gameState.savingsBalance + gameState.totalInvested - gameState.loan;
  };

  // Calculate total portfolio value
  const calculatePortfolioValue = () => {
    return gameState.portfolio.reduce((total, position) => {
      return total + (position.quantity * (position.currentPrice || position.averagePrice));
    }, 0);
  };

  // Buy asset function (FIFO accounting)
  const buyAsset = (asset, quantity, pricePerShare) => {
    const totalCost = quantity * pricePerShare;
    
    // Check if user has enough cash
    if (gameState.cash < totalCost) {
      throw new Error('Insufficient cash for this purchase');
    }

    const transaction = {
      id: Date.now(),
      type: 'buy',
      ticker: asset.ticker,
      assetName: asset.name,
      quantity: quantity,
      pricePerShare: pricePerShare,
      totalCost: totalCost,
      timestamp: new Date().toISOString(),
      gameDay: gameState.gameDay
    };

    setGameState(prevState => {
      // Find existing position or create new one
      const existingPositionIndex = prevState.portfolio.findIndex(
        position => position.ticker === asset.ticker
      );

      let newPortfolio;
      if (existingPositionIndex >= 0) {
        // Update existing position
        const existingPosition = prevState.portfolio[existingPositionIndex];
        const newTotalQuantity = existingPosition.quantity + quantity;
        const newTotalCost = existingPosition.totalCost + totalCost;
        const newAveragePrice = newTotalCost / newTotalQuantity;

        newPortfolio = [...prevState.portfolio];
        newPortfolio[existingPositionIndex] = {
          ...existingPosition,
          quantity: newTotalQuantity,
          totalCost: newTotalCost,
          averagePrice: newAveragePrice,
          currentPrice: pricePerShare, // Update current price
          lastUpdated: new Date().toISOString()
        };
      } else {
        // Create new position
        const newPosition = {
          ticker: asset.ticker,
          assetName: asset.name,
          quantity: quantity,
          averagePrice: pricePerShare,
          currentPrice: pricePerShare,
          totalCost: totalCost,
          lastUpdated: new Date().toISOString(),
          purchases: [{ // Track individual purchases for FIFO
            quantity: quantity,
            price: pricePerShare,
            timestamp: new Date().toISOString()
          }]
        };
        newPortfolio = [...prevState.portfolio, newPosition];
      }

      return {
        ...prevState,
        cash: prevState.cash - totalCost,
        totalInvested: prevState.totalInvested + totalCost,
        portfolio: newPortfolio,
        transactions: [...prevState.transactions, transaction],
        xpCurrent: prevState.xpCurrent + 10 // Award XP for trading
      };
    });

    return transaction;
  };

  // Sell asset function (FIFO accounting)
  const sellAsset = (ticker, quantity, pricePerShare) => {
    const positionIndex = gameState.portfolio.findIndex(
      position => position.ticker === ticker
    );

    if (positionIndex === -1) {
      throw new Error('Asset not found in portfolio');
    }

    const position = gameState.portfolio[positionIndex];
    
    if (position.quantity < quantity) {
      throw new Error('Insufficient shares to sell');
    }

    const totalProceeds = quantity * pricePerShare;
    
    // Calculate realized P&L using FIFO (use average price for simplicity)
    const realizedPnL = (pricePerShare - position.averagePrice) * quantity;

    const transaction = {
      id: Date.now(),
      type: 'sell',
      ticker: ticker,
      assetName: position.assetName,
      quantity: quantity,
      pricePerShare: pricePerShare,
      totalProceeds: totalProceeds,
      realizedPnL: realizedPnL,
      timestamp: new Date().toISOString(),
      gameDay: gameState.gameDay
    };

    setGameState(prevState => {
      const newPortfolio = [...prevState.portfolio];
      const updatedPosition = { ...position };

      if (updatedPosition.quantity === quantity) {
        // Remove position completely
        newPortfolio.splice(positionIndex, 1);
      } else {
        // Update position
        const remainingQuantity = updatedPosition.quantity - quantity;
        const remainingCost = updatedPosition.totalCost - (quantity * updatedPosition.averagePrice);
        
        updatedPosition.quantity = remainingQuantity;
        updatedPosition.totalCost = remainingCost;
        updatedPosition.currentPrice = pricePerShare;
        updatedPosition.lastUpdated = new Date().toISOString();
        
        newPortfolio[positionIndex] = updatedPosition;
      }

      return {
        ...prevState,
        cash: prevState.cash + totalProceeds,
        totalInvested: prevState.totalInvested - (quantity * position.averagePrice),
        portfolio: newPortfolio,
        transactions: [...prevState.transactions, transaction],
        realizedPnL: prevState.realizedPnL + realizedPnL,
        xpCurrent: prevState.xpCurrent + 15 // Award more XP for selling (harder decision)
      };
    });

    return transaction;
  };

  // Update asset prices in portfolio
  const updatePortfolioPrices = (priceUpdates) => {
    setGameState(prevState => {
      const newPortfolio = prevState.portfolio.map(position => {
        const priceUpdate = priceUpdates.find(update => update.ticker === position.ticker);
        if (priceUpdate) {
          return {
            ...position,
            currentPrice: priceUpdate.price,
            lastUpdated: new Date().toISOString()
          };
        }
        return position;
      });

      const newTotalInvested = newPortfolio.reduce((total, position) => {
        return total + (position.quantity * (position.currentPrice || position.averagePrice));
      }, 0);

      return {
        ...prevState,
        portfolio: newPortfolio,
        totalInvested: newTotalInvested
      };
    });
  };

  // Take loan function
  const takeLoan = (amount) => {
    if (amount <= 0 || amount > 1000) {
      throw new Error('Loan amount must be between $1 and $1,000');
    }

    if (gameState.loan > 0) {
      throw new Error('You already have an active loan');
    }

    setGameState(prevState => ({
      ...prevState,
      cash: prevState.cash + amount,
      loan: amount
    }));
  };

  // Repay loan function
  const repayLoan = (amount) => {
    if (amount <= 0 || amount > gameState.loan) {
      throw new Error('Invalid repayment amount');
    }

    if (gameState.cash < amount) {
      throw new Error('Insufficient cash to repay loan');
    }

    setGameState(prevState => ({
      ...prevState,
      cash: prevState.cash - amount,
      loan: prevState.loan - amount
    }));
  };

  // Deposit to savings
  const depositToSavings = (amount) => {
    if (amount <= 0 || amount > gameState.cash) {
      throw new Error('Invalid deposit amount');
    }

    setGameState(prevState => ({
      ...prevState,
      cash: prevState.cash - amount,
      savingsBalance: prevState.savingsBalance + amount
    }));
  };

  // Withdraw from savings
  const withdrawFromSavings = (amount) => {
    if (amount <= 0 || amount > gameState.savingsBalance) {
      throw new Error('Invalid withdrawal amount');
    }

    setGameState(prevState => ({
      ...prevState,
      cash: prevState.cash + amount,
      savingsBalance: prevState.savingsBalance - amount
    }));
  };

  // Apply daily interest
  const applyDailyInterest = () => {
    setGameState(prevState => {
      const savingsInterest = prevState.savingsBalance * prevState.savingsInterestRate;
      const loanInterest = prevState.loan * prevState.loanInterestRate;

      return {
        ...prevState,
        savingsBalance: prevState.savingsBalance + savingsInterest,
        loan: prevState.loan + loanInterest,
        gameDay: prevState.gameDay + 1,
        lastUpdate: new Date().toISOString().split('T')[0]
      };
    });
  };

  // Get level text
  const getLevelText = () => {
    switch(gameState.experienceLevel) {
      case 1: return 'BEGINNER INVESTOR';
      case 2: return 'APPRENTICE TRADER';
      case 3: return 'EXPERT ANALYST';
      case 4: return 'MASTER INVESTOR';
      default: return 'LEGENDARY TRADER';
    }
  };

  // Load real portfolio data from API with throttling
  const loadPortfolioData = async () => {
    const userId = user?._id || localStorage.getItem('userId');
    if (!userId || !user) {
      console.log('🎮 No user available for portfolio data');
      return;
    }
    
    // Throttle API calls to prevent 429 errors (minimum 2 seconds between calls)
    const now = Date.now();
    if (now - lastLoadTime < 2000) {
      console.log('API call throttled, using cached data');
      return;
    }
    
    setLastLoadTime(now);
    
    try {
      const portfolioResponse = await transactionService.getPortfolio(userId);
      
      if (portfolioResponse.success) {
        const portfolio = portfolioResponse.data;
        
        setGameState(prev => ({
          ...prev,
          cash: portfolio.cash || 0,
          loan: portfolio.loan || 0,
          savingsBalance: portfolio.deposit || 0,
          totalInvested: portfolio.totalAssetsValue || 0,
          // Keep other non-financial state
        }));
      } else {
        console.error('Portfolio API returned error:', portfolioResponse.message);
      }
    } catch (error) {
      console.error('Error loading portfolio data in GameContext:', error);
      
      // If it's a 429 error, set a default state for new users
      if (error.response?.status === 429) {
        console.log('Rate limited, using default values for new user');
        setGameState(prev => ({
          ...prev,
          cash: 1000, // Default starting cash for new users
          loan: 0,
          savingsBalance: 0,
          totalInvested: 0,
        }));
      }
    }
  };

  // Reset game (for testing or new game)
  const resetGame = () => {
    setGameState({
      cash: 0, // Will be populated from real API data
      savingsBalance: 0,
      loan: 0,
      totalInvested: 0,
      portfolio: [],
      loanInterestRate: 0.04 / 365,
      savingsInterestRate: 0.03 / 365,
      experienceLevel: 1,
      achievementPoints: 0,
      xpCurrent: 0,
      xpToNextLevel: 100,
      tutorialStatus: 'available',
      gameDay: 1,
      lastUpdate: new Date().toISOString().split('T')[0],
      transactions: [],
      realizedPnL: 0,
      achievements: [],
      streaks: {
        dailyLogin: 0,
        profitableDays: 0
      }
    });
    
    // Remove user-specific game data
    const storageKey = getGameStorageKey();
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
    
    // Also remove old non-user-specific key for migration
    localStorage.removeItem('kidsInvestmentGame');
  };

  // Clear all user data (called when user logs out)
  const clearUserData = () => {
    console.log('🎮 Clearing all user game data');
    clearPreviousUserData();
    resetGame();
  };

  const value = {
    gameState,
    setGameState,
    calculateNetWorth,
    calculatePortfolioValue,
    buyAsset,
    sellAsset,
    updatePortfolioPrices,
    takeLoan,
    repayLoan,
    depositToSavings,
    withdrawFromSavings,
    applyDailyInterest,
    getLevelText,
    resetGame,
    loadPortfolioData,
    clearUserData
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};